// Counterpart of webapp/src/store/cards.ts

import {create} from 'zustand'
import {Card, createCard} from '../types/card'
import {Block} from '../types/block'
import client from '../api/client'

type CardsState = {
    cards: Record<string, Card>
    loadCards: (boardId: string) => Promise<Card[]>
    hydrateBlocks: (blocks: Block[]) => void
    upsertCards: (cards: Card[]) => void
    upsertCard: (card: Card) => void
    removeCard: (cardId: string) => void
    archiveCard: (cardId: string, deleteAt: number) => void
}

export const useCards = create<CardsState>((set, get) => ({
    cards: {},

    async loadCards(boardId) {
        const blocks = await client.getBlocksWithParent(boardId, 'card')
        const cards = blocks.map((b) => createCard(b))
        get().hydrateBlocks(cards)
        return cards
    },

    hydrateBlocks(blocks) {
        const cards = blocks.filter((b) => b.type === 'card').map((b) => createCard(b))
        get().upsertCards(cards)
    },

    upsertCards(cards) {
        set((state) => {
            const next = {...state.cards}
            cards.forEach((c) => {
                next[c.id] = c
            })
            return {cards: next}
        })
    },

    upsertCard(card) {
        get().upsertCards([card])
    },

    removeCard(cardId) {
        set((state) => {
            const cards = {...state.cards}
            delete cards[cardId]
            return {cards}
        })
    },

    archiveCard(cardId, deleteAt) {
        const card = get().cards[cardId]
        if (card) {
            get().upsertCard({...card, deleteAt})
        }
    },
}))

export function selectCardsOfBoard(state: CardsState, boardId: string | undefined, includeArchived = false): Card[] {
    if (!boardId) {
        return []
    }
    return Object.values(state.cards).
        filter((c) => c.boardId === boardId && (includeArchived || c.deleteAt === 0) && !c.fields.isTemplate)
}
